/**
 * The remaining committee forms: race results, events, volunteer work,
 * manual hours, notices, replies, the safety contact, and the department
 * team roster.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  bibNo,
  DISTS,
  eventLinkError,
  formatDate,
  formatMoney,
  isPersonalBest,
  normalizeEventLink,
  parseTime,
  today,
} from '../../data';
import { useClub } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { EVENT_IMAGE, pickAndResizePhoto } from '../../lib/image';
import { Avatar } from '../Avatar';
import { Button, Empty, Field, FormError, Hint, Pill, Row2, SheetActions, SheetHead, TEXTAREA_CLS } from '../ui';

/* ---------------- race result ---------------- */

/*
 * `event` locks the sheet to one event: the race name and date come from it and
 * cannot be edited, and the result is filed against it. That is how an event
 * HEAD logs their runners — their permission is to record THAT event, so the
 * sheet must not offer them a free-text race name to file it under.
 */
export function ResultSheet({ db, presetMemberId, event }) {
  const { addResult } = useClub();
  const { closeSheet, toast } = useUI();

  const roster = [...db.members].sort((a, b) => a.bib - b.bib);
  const [memberId, setMemberId] = useState(presetMemberId ?? roster[0]?.id ?? '');
  const [race, setRace] = useState(event?.title ?? '');
  const [dist, setDist] = useState('5K'); // a DISTS key
  const [time, setTime] = useState('');
  const [date, setDate] = useState(event?.date ?? today());
  const [error, setError] = useState(null);

  async function save() {
    if (!race.trim()) return setError('Race name is required.');
    const seconds = parseTime(time);
    // > 0, not just parseable: "0:00" parses fine and would become an
    // unbeatable personal best for that distance, for good.
    if (seconds === null || seconds <= 0) {
      return setError('Enter time as h:mm:ss or mm:ss.');
    }

    // Decide PB before the write, comparing against every existing result.
    const isPb = isPersonalBest(db.results, memberId, dist, seconds);

    try {
      await addResult({
        memberId,
        race: race.trim(),
        dist,
        timeS: seconds,
        date: date || today(),
        eventId: event?.id ?? null,
      });
    } catch (err) {
      // Without this the sheet sat open doing nothing on a rejected save.
      return setError(err instanceof Error ? err.message : 'Could not save that result.');
    }
    closeSheet();

    if (isPb) {
      toast(`New ${dist} personal best — ${time.trim()}`, 'pb');
    } else {
      toast('Result saved');
    }
  }

  return (
    <>
      <SheetHead
        title="Log a race result"
        sub={
          event
            ? `${event.title} — a personal best is detected automatically`
            : 'A personal best is detected automatically'
        }
      />

      <Field label="Runner" required>
        <select value={memberId} onChange={(e) => setMemberId(e.target.value)}>
          {roster.map((m) => (
            <option key={m.id} value={m.id}>
              {bibNo(m.bib)} · {m.name}
            </option>
          ))}
        </select>
      </Field>

      {!event && (
        <Field label="Race name" required>
          <input
            value={race}
            onChange={(e) => setRace(e.target.value)}
            placeholder="e.g. Tirupur Runners Marathon"
          />
        </Field>
      )}

      <Row2>
        <Field label="Distance" required>
          <select value={dist} onChange={(e) => setDist(e.target.value)}>
            {DISTS.map((d) => (
              <option key={d.key} value={d.key}>
                {d.key}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Chip time" required>
          <input
            value={time}
            onChange={(e) => setTime(e.target.value)}
            placeholder="h:mm:ss or mm:ss"
          />
        </Field>
      </Row2>

      {!event && (
        <Field label="Race date" required>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
      )}

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="d" onClick={save}>
          Save result
        </Button>
      </SheetActions>
    </>
  );
}

/* ---------------- event ---------------- */

export function EventSheet() {
  const { addEvent } = useClub();
  const { closeSheet, toast } = useUI();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [kind, setKind] = useState('run'); // 'run' | 'race' | 'meeting' | 'volunteer'
  const [date, setDate] = useState(today());
  const [time, setTime] = useState('06:00');
  const [venue, setVenue] = useState('');
  const [distanceKm, setDistanceKm] = useState('');
  const [note, setNote] = useState('');
  // The event's own web page, when it has one. Can also be added later from
  // the event page — most races get their link after the date is announced.
  const [linkUrl, setLinkUrl] = useState('');
  // A resized JPEG data URL until save, when ClubContext uploads it to
  // Storage and only the URL travels — same discipline as chat images.
  const [image, setImage] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  async function pickImage() {
    try {
      setImage(await pickAndResizePhoto(EVENT_IMAGE));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read that image.');
    }
  }

  // Only something you run has a distance. A meeting keeps its field hidden and
  // sends null, so it can never land on the club's distance total.
  const hasDistance = kind === 'run' || kind === 'race';

  async function save() {
    if (!title.trim()) return setError('Title is required.');
    if (!venue.trim()) return setError('Venue is required.');
    const km = hasDistance && distanceKm.trim() ? Number(distanceKm) : null;
    if (km !== null && (!Number.isFinite(km) || km <= 0)) {
      return setError('Distance must be a number of kilometres.');
    }
    const link = normalizeEventLink(linkUrl);
    const linkProblem = eventLinkError(link);
    if (linkProblem) return setError(linkProblem);

    setSaving(true);
    try {
      const id = await addEvent({
        title: title.trim(),
        kind,
        date: date || today(),
        time: time || '06:00',
        venue: venue.trim(),
        distanceKm: km,
        note: note.trim(),
        linkUrl: link,
        image,
      });
      closeSheet();
      toast('Event created — post volunteer work below');
      /*
       * Land on the event itself. Volunteer departments — the whole point of
       * a race — are posted from INSIDE an event, and the create sheet used to
       * close back onto the list, leaving the committee to work that out for
       * themselves (owner's direction, 2 Sep 2026).
       */
      if (id) navigate(`/a/events/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create that event.');
      setSaving(false);
    }
  }

  return (
    <>
      <SheetHead title="Create event" sub="A run, race, meeting or volunteer duty" />

      <Field label="Title" required>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Monthly Committee Meeting"
        />
      </Field>

      <Field label="Type" required>
        <select value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="run">Group run</option>
          <option value="race">Race</option>
          <option value="meeting">Meeting</option>
          <option value="volunteer">Volunteer duty</option>
        </select>
      </Field>

      <Row2>
        <Field label="Date" required>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Time" required>
          <input value={time} onChange={(e) => setTime(e.target.value)} placeholder="06:00" />
        </Field>
      </Row2>

      <Field label="Venue" required>
        <input
          value={venue}
          onChange={(e) => setVenue(e.target.value)}
          placeholder="e.g. Nanjappa School Grounds"
        />
      </Field>

      {/* The club's headline distance is the sum of THIS number across every
          event the committee has marked completed. Fill it in and a finished
          race puts its kilometres on the board. */}
      {hasDistance && (
        <Field label="Distance (km)">
          <input
            type="number"
            min="0"
            step="0.1"
            inputMode="decimal"
            value={distanceKm}
            onChange={(e) => setDistanceKm(e.target.value)}
            placeholder="e.g. 21"
          />
          <div className="mt-1.5 font-market text-[11px] leading-relaxed text-copy-label">
            Counts toward the club&rsquo;s total distance once you mark this event completed.
          </div>
        </Field>
      )}

      <Field label="Note for members">
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional details"
        />
      </Field>

      {/* Races are often run off a site of their own — entries, categories,
          the route map. Paste it here and every member's card carries a link
          straight to it. */}
      <Field label="Event page link">
        <input
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          placeholder="e.g. tirupurmarathon.in/register"
          inputMode="url"
        />
        <Hint>
          Optional. For a race with its own website or registration form — members get an
          &ldquo;Event page&rdquo; link that opens in a new tab.
        </Hint>
      </Field>

      {/* The banner. Optional — but it is what turns a text row into the
          poster-style card on the events screens. */}
      <Field label="Event image">
        {image ? (
          <div className="relative overflow-hidden border border-line">
            <img src={image} alt="Event banner preview" className="h-36 w-full object-cover" />
            <div className="absolute right-2 top-2 flex gap-1.5">
              <Button small onClick={() => void pickImage()}>
                Replace
              </Button>
              <Button small onClick={() => setImage(null)}>
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => void pickImage()}
            className="flex w-full items-center justify-center gap-2 border border-dashed border-line-strong bg-ground px-3 py-6 font-market text-[11px] uppercase tracking-wide text-copy-label transition-colors duration-200 hover:border-ink-700 hover:text-copy"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="m21 15-5-5L5 21" />
            </svg>
            Upload a banner image
          </button>
        )}
        <Hint>
          A poster, the route, or last year&rsquo;s finish line. Shown as the hero at the top of
          the event page.
        </Hint>
      </Field>

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="p" onClick={save} disabled={saving}>
          {saving ? 'Creating…' : 'Create event'}
        </Button>
      </SheetActions>
    </>
  );
}

/* ---------------- volunteer department ---------------- */

export function DepartmentSheet({ db, presetEventId }) {
  const { addDepartment } = useClub();
  const { closeSheet, toast } = useUI();

  /* Upcoming events, plus whichever event this sheet was opened from — a
     preset that is not in the list left the dropdown blank and let the
     department be saved against a different event entirely. */
  const upcoming = db.events.filter(
    (e) => e.date >= today() || e.id === presetEventId,
  );

  const [eventId, setEventId] = useState(presetEventId ?? upcoming[0]?.id ?? '');
  const [name, setName] = useState('');
  const [zone, setZone] = useState('');
  const [needed, setNeeded] = useState('6');
  const [hours, setHours] = useState('4');
  const [lead, setLead] = useState('');
  const [task, setTask] = useState('');
  const [error, setError] = useState(null);

  async function save() {
    if (!eventId) return setError('Pick an event.');
    if (!name.trim() || !zone.trim() || !lead.trim() || !task.trim()) {
      return setError('All fields are required.');
    }
    try {
      await addDepartment({
        eventId,
        name: name.trim(),
        zone: zone.trim(),
        needed: Number.parseInt(needed, 10) || 1,
        hours: Number.parseFloat(hours) || 1,
        lead: lead.trim(),
        task: task.trim(),
      });
    } catch (err) {
      return setError(err instanceof Error ? err.message : 'Could not post that work.');
    }
    closeSheet();
    toast('Volunteer work posted');
  }

  return (
    <>
      <SheetHead title="Post volunteer work" sub="A department members can sign up for" />

      <Field label="Event" required>
        <select value={eventId} onChange={(e) => setEventId(e.target.value)}>
          {upcoming.map((e) => (
            <option key={e.id} value={e.id}>
              {e.title} · {formatDate(e.date)}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Department name" required>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Aid Station 7"
        />
      </Field>

      <Field label="Zone / location" required>
        <input
          value={zone}
          onChange={(e) => setZone(e.target.value)}
          placeholder="e.g. Course KM 12"
        />
      </Field>

      <Row2>
        <Field label="Volunteers needed" required>
          <input type="number" value={needed} onChange={(e) => setNeeded(e.target.value)} />
        </Field>
        <Field label="Hours credit" required>
          <input
            type="number"
            step="0.5"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
          />
        </Field>
      </Row2>

      <Field label="Department lead" required>
        <input
          value={lead}
          onChange={(e) => setLead(e.target.value)}
          placeholder="Who runs this team"
        />
      </Field>

      <Field label="What the work involves" required>
        <textarea
          className={TEXTAREA_CLS}
          value={task}
          onChange={(e) => setTask(e.target.value)}
          placeholder="Describe the duty clearly so members know what they're signing up for"
        />
      </Field>

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="v" onClick={save}>
          Post work
        </Button>
      </SheetActions>
    </>
  );
}

/** Who is on a department's team, and whether they are confirmed yet. */
export function DeptTeamSheet({ dept, db }) {
  const { closeSheet } = useUI();
  const signups = db.signups.filter((s) => s.deptId === dept.id);
  const team = signups
    .map((s) => ({ signup: s, member: db.members.find((m) => m.id === s.memberId) }))
    .filter((x) => !!x.member);

  return (
    <>
      <SheetHead
        title={dept.name}
        sub={`${dept.zone} · ${signups.length}/${dept.needed} filled · ${dept.hours} hrs each`}
      />

      {team.length ? (
        team.map(({ signup, member }) => (
          <div
            key={signup.id}
            className="flex items-center gap-3 border-b border-line py-2.5 last:border-b-0"
          >
            <Avatar member={member} className="h-[34px] w-[34px] flex-none text-xs" />
            <div className="min-w-0">
              <div className="text-[13.5px] font-medium">
                {member.name} · bib {bibNo(member.bib)}
              </div>
              <div className="mt-0.5 font-market text-[10px] text-copy-label">
                {member.phone ?? ''}
              </div>
            </div>
            <span className="ml-auto">
              <Pill tone={signup.status === 'confirmed' ? 'ok' : 'soon'}>
                {signup.status === 'confirmed' ? 'Confirmed' : 'Signed up'}
              </Pill>
            </span>
          </div>
        ))
      ) : (
        <Empty padding={26}>No one signed up yet.</Empty>
      )}

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Close
        </Button>
      </SheetActions>
    </>
  );
}

/* ---------------- manual volunteer hours ---------------- */

const DUTIES = [
  'Aid station',
  'Marshal',
  'Pacer',
  'Photographer',
  'Registration desk',
  'Setup',
  'Other',
];

/* `event` locks the entry to one event — see ResultSheet above for why. */
export function VolunteerHoursSheet({ db, presetMemberId, event }) {
  const { logVolunteerHours } = useClub();
  const { closeSheet, toast } = useUI();

  const roster = [...db.members].sort((a, b) => a.bib - b.bib);
  const [memberId, setMemberId] = useState(presetMemberId ?? roster[0]?.id ?? '');
  const [duty, setDuty] = useState(DUTIES[0]);
  const [date, setDate] = useState(event?.date ?? today());
  const [hours, setHours] = useState('');
  const [error, setError] = useState(null);

  async function save() {
    const value = Number.parseFloat(hours);
    if (!value || value <= 0) return setError('Enter valid hours.');
    try {
      await logVolunteerHours({
        memberId,
        role: duty,
        date: date || today(),
        hours: value,
        eventId: event?.id ?? null,
      });
    } catch (err) {
      return setError(err instanceof Error ? err.message : 'Could not log those hours.');
    }
    closeSheet();
    toast(`${value} hrs logged`);
  }

  return (
    <>
      <SheetHead
        title="Log volunteer hours"
        sub={event ? event.title : 'Counts toward the member\u2019s yearly hours'}
      />

      <Field label="Member" required>
        <select value={memberId} onChange={(e) => setMemberId(e.target.value)}>
          {roster.map((m) => (
            <option key={m.id} value={m.id}>
              {bibNo(m.bib)} · {m.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Duty" required>
        <select value={duty} onChange={(e) => setDuty(e.target.value)}>
          {DUTIES.map((d) => (
            <option key={d}>{d}</option>
          ))}
        </select>
      </Field>

      {event ? (
        <Field label="Hours" required>
          <input
            type="number"
            step="0.5"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder="4"
          />
        </Field>
      ) : (
        <Row2>
          <Field label="Date" required>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label="Hours" required>
            <input
              type="number"
              step="0.5"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="4"
            />
          </Field>
        </Row2>
      )}

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="v" onClick={save}>
          Log hours
        </Button>
      </SheetActions>
    </>
  );
}

/**
 * Put a member in charge of an event, or stand them down.
 *
 * The head gains real power — logging results and hours for the whole club on
 * this event — so the sheet says so plainly rather than presenting itself as a
 * label. Only ACTIVE members are offered: a paused or departed member should
 * not be handed a job.
 */
export function EventHeadSheet({ db, event }) {
  const { setEventHead } = useClub();
  const { closeSheet, toast } = useUI();

  const roster = [...db.members]
    .filter((m) => m.status === 'active')
    .sort((a, b) => a.bib - b.bib);
  const [headId, setHeadId] = useState(event.headId ?? '');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const current = db.members.find((m) => m.id === event.headId);

  async function save() {
    setSaving(true);
    try {
      await setEventHead(event.id, headId || null);
    } catch (err) {
      setSaving(false);
      return setError(err instanceof Error ? err.message : 'Could not save that.');
    }
    closeSheet();
    const picked = db.members.find((m) => m.id === headId);
    toast(picked ? `${picked.name} is leading ${event.title}` : 'Event head removed');
  }

  return (
    <>
      <SheetHead
        title="Who is leading this event?"
        sub={current ? `Currently ${current.name}` : 'Nobody is leading it yet'}
      />

      <Field label="Event head">
        <select value={headId} onChange={(e) => setHeadId(e.target.value)}>
          <option value="">— Nobody —</option>
          {roster.map((m) => (
            <option key={m.id} value={m.id}>
              {bibNo(m.bib)} · {m.name}
            </option>
          ))}
        </select>
        <div className="mt-1.5 font-market text-[11px] leading-relaxed text-copy-label">
          They get this event in their own menu and can log race results and volunteer hours for
          every member who took part. It disappears when you mark the event completed.
        </div>
      </Field>

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="v" disabled={saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </SheetActions>
    </>
  );
}

/* ---------------- the event's own web page ---------------- */

/**
 * Add, change or clear an event's external page.
 *
 * A sheet of its own rather than a field on an edit form, because events have
 * no edit form at all: the registration link is the one thing that reliably
 * arrives AFTER the date is posted and the RSVPs have started, and "delete it
 * and make it again" would take the RSVPs with it.
 */
export function EventLinkSheet({ event }) {
  const { setEventLink } = useClub();
  const { closeSheet, toast } = useUI();
  const [linkUrl, setLinkUrl] = useState(event.linkUrl ?? '');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    const link = normalizeEventLink(linkUrl);
    const problem = eventLinkError(link);
    if (problem) return setError(problem);
    setSaving(true);
    try {
      await setEventLink(event.id, link);
    } catch (err) {
      setSaving(false);
      return setError(err instanceof Error ? err.message : 'Could not save that link.');
    }
    closeSheet();
    toast(link ? 'Event page link saved' : 'Event page link removed');
  }

  return (
    <>
      <SheetHead
        title="Event page link"
        sub={event.title}
      />

      <Field label="Web address">
        <input
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          placeholder="e.g. tirupurmarathon.in/register"
          inputMode="url"
        />
        <Hint>
          The race site, the registration form, the timing partner — wherever this event really
          lives. Members see an &ldquo;Event page&rdquo; link on the card and on this page, and it
          opens in a new tab. Clear the box to remove it.
        </Hint>
      </Field>

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="v" disabled={saving} onClick={save}>
          {saving ? 'Saving…' : 'Save link'}
        </Button>
      </SheetActions>
    </>
  );
}

/* ---------------- the event's figures ---------------- */

/** '' → null (not entered); otherwise a whole number ≥ 0, or an error. */
function readCount(raw, what) {
  const t = String(raw).replace(/[,\s₹]/g, '');
  if (t === '') return { value: null };
  if (!/^\d+$/.test(t)) return { error: `${what} must be a whole number.` };
  return { value: Number(t) };
}

/**
 * Total registered and amount collected for one event, typed by the committee
 * — entries mostly happen off the app (the race's own form, cash at the
 * meeting), so there is nothing to count them from. One switch shows both to
 * members; off by default, because money on screen should be a decision.
 */
export function EventFiguresSheet({ event }) {
  const { setEventFigures } = useClub();
  const { closeSheet, toast } = useUI();
  const [registered, setRegistered] = useState(
    event.registeredCount == null ? '' : String(event.registeredCount),
  );
  const [amount, setAmount] = useState(
    event.amountCollected == null ? '' : String(event.amountCollected),
  );
  const [shown, setShown] = useState(Boolean(event.figuresPublic));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    const r = readCount(registered, 'Registered');
    const a = readCount(amount, 'Amount collected');
    if (r.error || a.error) return setError(r.error ?? a.error);
    setSaving(true);
    try {
      await setEventFigures(event.id, {
        registeredCount: r.value,
        amountCollected: a.value,
        figuresPublic: shown,
      });
    } catch (err) {
      setSaving(false);
      return setError(err instanceof Error ? err.message : 'Could not save the figures.');
    }
    closeSheet();
    toast(shown ? 'Figures saved — members can see them' : 'Figures saved — committee only');
  }

  return (
    <>
      <SheetHead title="Registrations & collection" sub={event.title} />

      <Row2>
        <Field label="Total registered">
          <input
            value={registered}
            onChange={(e) => setRegistered(e.target.value)}
            placeholder="e.g. 120"
            inputMode="numeric"
          />
        </Field>
        <Field label="Amount collected (₹)">
          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 60000"
            inputMode="numeric"
          />
        </Field>
      </Row2>
      <Hint>
        Everyone registered for this event, wherever they signed up — not only the club&rsquo;s
        RSVPs. Leave a box empty if you do not have that figure yet.
      </Hint>

      <label className="mt-4 flex cursor-pointer items-start gap-2.5 border border-line bg-white px-3 py-2.5">
        <input
          type="checkbox"
          checked={shown}
          onChange={(e) => setShown(e.target.checked)}
          className="mt-[3px] flex-none accent-gold-500"
        />
        <span className="text-[12.5px] leading-relaxed text-copy-muted">
          <b className="text-ink-700">Show to members</b> — they see both figures on this event.
          Off, only the committee sees them.
          {shown && readCount(amount, '').value != null && (
            <> Members will see {formatMoney(readCount(amount, '').value)} collected.</>
          )}
        </span>
      </label>

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="v" disabled={saving} onClick={save}>
          {saving ? 'Saving…' : 'Save figures'}
        </Button>
      </SheetActions>
    </>
  );
}

/* ---------------- notices and replies ---------------- */

export function NoticeSheet({ memberCount }) {
  const { sendNotice } = useClub();
  const { closeSheet, toast } = useUI();
  const [text, setText] = useState('');
  const [error, setError] = useState(null);

  async function send() {
    if (!text.trim()) return setError('Write a message first.');
    try {
      await sendNotice(text.trim());
    } catch (err) {
      return setError(err instanceof Error ? err.message : 'Could not send that notice.');
    }
    closeSheet();
    toast('Notification sent to all members');
  }

  return (
    <>
      <SheetHead
        title="Send notification"
        sub={`Goes to all ${memberCount} members · shows in their Notices tab`}
      />

      <Field label="Message" required>
        <textarea
          className={TEXTAREA_CLS}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. Sunday long run moved to 5:00 AM due to heat."
        />
      </Field>

      <Hint>In the live app this also sends a WhatsApp message.</Hint>
      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="p" onClick={send}>
          Send to all
        </Button>
      </SheetActions>
    </>
  );
}

/**
 * A notice addressed to one member, sent from their detail page.
 *
 * Deliberately the same `kind: 'notice'` as a broadcast rather than a new
 * message type: it lands in the same Notices tab and the same bell count, so
 * a member has one inbox, not two. The only difference is `to`.
 */
export function MessageMemberSheet({ member }) {
  const { sendMemberNotice } = useClub();
  const { closeSheet, toast } = useUI();
  const [text, setText] = useState('');
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  async function send() {
    if (!text.trim()) return setError('Write a message first.');
    setSending(true);
    try {
      await sendMemberNotice(member.id, text.trim());
      closeSheet();
      toast(`Message sent to ${member.name}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send.');
      setSending(false);
    }
  }

  return (
    <>
      <SheetHead
        title="Message member"
        sub={`${bibNo(member.bib)} · ${member.name} — shows in their Notices tab`}
      />

      <Field label="Message" required>
        <textarea
          className={TEXTAREA_CLS}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. Your dues are pending — pay link sent to your mobile."
        />
      </Field>

      <Hint>Only this member sees it. In the live app it also sends a WhatsApp message.</Hint>
      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="p" onClick={send} disabled={sending}>
          Send message
        </Button>
      </SheetActions>
    </>
  );
}

/**
 * Removing a member is a status change, never a delete (CLAUDE.md §10.7).
 *
 * The bib stays retired to them, their results stay in the club's totals, and
 * their receipts stay auditable. What actually changes is that they can no
 * longer sign in — `resolveAccount` denies a 'left' member with LEFT_CLUB.
 * The copy below says all of that, because "remove" sounds irreversible and
 * an admin should know it is not before they click.
 */
export function RemoveMemberSheet({ member, onRemoved }) {
  const { removeMember } = useClub();
  const { closeSheet, toast } = useUI();
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(false);

  const expected = bibNo(member.bib);

  async function remove() {
    if (confirm.trim() !== expected) {
      return setError(`Type ${expected} to confirm.`);
    }
    setWorking(true);
    try {
      await removeMember(member.id);
      closeSheet();
      toast(`${member.name} marked as left`);
      onRemoved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove.');
      setWorking(false);
    }
  }

  return (
    <>
      <SheetHead title="Remove from the club?" sub={`${expected} · ${member.name}`} />

      <Hint>
        This marks them as <b>left</b> rather than deleting anything. Bib {expected} stays
        retired to them and is never reused, and their race results, volunteer hours and
        receipts all stay on the club record.
      </Hint>

      <div style={{ marginTop: 14 }}>
        <Hint>
          What changes: they can no longer sign in, and they stop appearing in the active
          roster. You can put them back by editing their status.
        </Hint>
      </div>

      <div style={{ marginTop: 14 }}>
        <Field label={`Type the bib number ${expected} to confirm`} required>
          <input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={expected}
          />
        </Field>
      </div>

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Keep on roster
        </Button>
        <Button tone="r" onClick={remove} disabled={working}>
          Remove member
        </Button>
      </SheetActions>
    </>
  );
}

export function ReplySheet({ messageId, originalText, memberName }) {
  const { replyToMessage } = useClub();
  const { closeSheet, toast } = useUI();
  const [text, setText] = useState('');
  const [error, setError] = useState(null);

  async function send() {
    if (!text.trim()) return setError('Write a reply first.');
    try {
      await replyToMessage(messageId, text.trim());
    } catch (err) {
      return setError(err instanceof Error ? err.message : 'Could not send that reply.');
    }
    closeSheet();
    toast('Reply sent');
  }

  return (
    <>
      <SheetHead title="Reply" sub={`To ${memberName}`} />

      <div className="mb-3.5 border border-line border-l-[3px] border-l-line-strong bg-white p-3.5">
        <div className="text-[13.5px] leading-relaxed text-copy-muted">{originalText}</div>
      </div>

      <Field label="Your reply" required>
        <textarea className={TEXTAREA_CLS} value={text} onChange={(e) => setText(e.target.value)} />
      </Field>

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="p" onClick={send}>
          Send reply
        </Button>
      </SheetActions>
    </>
  );
}

/* ---------------- club safety contact ---------------- */

export function SafetyContactSheet({ name: initialName, phone: initialPhone }) {
  const { setSafetyContact } = useClub();
  const { closeSheet, toast } = useUI();
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState(initialPhone);
  const [error, setError] = useState(null);

  async function save() {
    if (!name.trim() || !phone.trim()) return setError('Both fields are required.');
    try {
      await setSafetyContact(name.trim(), phone.trim());
    } catch (err) {
      return setError(err instanceof Error ? err.message : 'Could not save that contact.');
    }
    closeSheet();
    toast('Safety contact updated');
  }

  return (
    <>
      <SheetHead
        title="Club safety contact"
        sub="Shown to every member on their emergency card"
      />

      <Field label="Name / role" required>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Muthu Selvam (Run Lead)"
        />
      </Field>

      <Field label="Mobile number" required>
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+9198…" />
      </Field>

      <Hint>Pick someone who is at the grounds most mornings and can reach a vehicle.</Hint>
      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="p" onClick={save}>
          Save
        </Button>
      </SheetActions>
    </>
  );
}

/* ---------------- decline a join request ---------------- */

export function DeclineRequestSheet({ requestId, name, phone }) {
  const { declineRequest } = useClub();
  const { closeSheet, toast } = useUI();
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  async function decline() {
    try {
      await declineRequest(requestId, reason.trim() || null);
    } catch (err) {
      return setError(err instanceof Error ? err.message : 'Could not decline that request.');
    }
    closeSheet();
    toast('Request declined');
  }

  return (
    <>
      <SheetHead title="Decline request?" sub={`${name} · ${phone}`} />

      <Hint>
        They won&rsquo;t be added to the roster. You can note a reason for your own records.
      </Hint>

      <div style={{ marginTop: 14 }}>
        <Field label="Reason (optional)">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. duplicate request"
          />
        </Field>
      </div>

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Keep pending
        </Button>
        <Button tone="r" onClick={decline}>
          Decline
        </Button>
      </SheetActions>
    </>
  );
}

/* ---------------- confirm a destructive action ---------------- */

/**
 * One confirmation sheet for the actions that destroy something.
 *
 * Removing an event took its departments and every sign-up with it, removing a
 * department took its volunteers' places, and removing a ground changed a
 * setting every member form reads — all on a single unguarded tap, on a phone
 * (owner's report, 2 Sep 2026). `RemoveMemberSheet` already did this properly;
 * this is the same discipline for the rest, without the bib-typing ceremony
 * that only a permanent record deserves.
 *
 * Props: { title, body, confirmLabel, onConfirm, doneMessage }
 */
export function ConfirmSheet({ title, body, confirmLabel, onConfirm, doneMessage }) {
  const { closeSheet, toast } = useUI();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function go() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      closeSheet();
      if (doneMessage) toast(doneMessage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not do that.');
      setBusy(false);
    }
  }

  return (
    <>
      <SheetHead title={title} />
      <p className="text-[13.5px] leading-relaxed text-copy-muted">{body}</p>
      <FormError>{error}</FormError>
      <SheetActions>
        <Button tone="g" onClick={closeSheet} disabled={busy}>
          Cancel
        </Button>
        <Button tone="r" onClick={go} disabled={busy}>
          {busy ? 'Working…' : confirmLabel}
        </Button>
      </SheetActions>
    </>
  );
}
